import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Material Filled Button
        default:
          "bg-primary text-white rounded-full shadow-md hover:shadow-lg hover:brightness-105 active:scale-[0.98] active:shadow-sm",
        // Material Filled Tonal Button
        secondary:
          "bg-secondary text-secondary-foreground rounded-full hover:brightness-95 active:scale-[0.98]",
        // Material Outlined Button
        outline:
          "border border-input bg-transparent rounded-full hover:bg-accent/50 hover:border-primary/50 active:scale-[0.98]",
        // Material Text Button
        ghost:
          "rounded-full hover:bg-accent/50 hover:text-accent-foreground active:bg-accent/70",
        // Link style
        link:
          "text-primary underline-offset-4 hover:underline hover:text-primary/80",
        // Material Filled Error Button
        destructive:
          "bg-destructive text-white rounded-full shadow-md hover:shadow-lg hover:brightness-105 active:scale-[0.98]",
        // Material Filled Success Button
        success:
          "bg-success text-white rounded-full shadow-md hover:shadow-lg hover:brightness-105 active:scale-[0.98]",
        // Material Elevated Button
        elevated:
          "bg-card text-card-foreground rounded-full shadow-md hover:shadow-lg active:scale-[0.98]",
        // Material Tonal Button
        tonal:
          "bg-primary/10 text-primary rounded-full hover:bg-primary/20 active:scale-[0.98]",
      },
      size: {
        default: "h-10 px-6 py-2",
        sm: "h-9 px-4 text-xs",
        lg: "h-12 px-8 text-base",
        xl: "h-14 px-10 text-lg",
        icon: "h-10 w-10 rounded-full",
        "icon-sm": "h-8 w-8 rounded-full",
        "icon-lg": "h-12 w-12 rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    // Empêche le comportement submit par défaut quand le bouton est rendu
    // dans un <form> (cause de reloads inattendus). Override possible via
    // la prop `type` pour les cas où on veut un vrai submit.
    const buttonType = asChild ? undefined : (type ?? "button")
    return (
      <Comp
        type={buttonType}
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
